import axios from "axios";
import { API_ERROR, isApiError } from "./apiParams";

const ACCESS_TOKEN_KEY = "theatres_access_token";
const USER_STORAGE_KEY = "theatres_user";

const baseUrl = process.env.REACT_APP_BASE_URL;

let refreshInFlight = null;
let sessionInvalidated = false;

export const isAuthApiError = (data) => isApiError(data, API_ERROR.AUTH);

/** login/refresh/logout — error: 3 здесь ожидаем, перехватчик не должен вызывать logout. */
export const isAuthSessionEndpoint = (url = "") =>
    /\/php\/models\/login\/(refresh|check)\.php/i.test(url) ||
    /\/php\/models\/login\/logout\.php/i.test(url);

export const invalidateAuthSession = () => {
    sessionInvalidated = true;
    refreshInFlight = null;
};

export const resetAuthSessionInvalidation = () => {
    sessionInvalidated = false;
};

export const redirectToLogin = () => {
    if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
    }
};

export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);

/** JWT access без проверки подписи — только срок действия (для dev без refresh-cookie). */
export const isAccessTokenExpired = (token) => {
    if (!token) {
        return true;
    }

    try {
        const part = token.split(".")[1];
        const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
        const payload = JSON.parse(json);

        return !payload.exp || payload.exp * 1000 <= Date.now();
    } catch {
        return true;
    }
};

export const setAccessToken = (token) => {
    if (token) {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    } else {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    }
    applyAuthHeader();
};

export const getStoredUser = () => {
    const raw = sessionStorage.getItem(USER_STORAGE_KEY);

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

export const setStoredUser = (user) => {
    if (user) {
        sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
        sessionStorage.removeItem(USER_STORAGE_KEY);
    }
};

export const applyAuthHeader = () => {
    const token = getAccessToken();

    if (token) {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        axios.defaults.headers.post.Authorization = `Bearer ${token}`;
    } else {
        delete axios.defaults.headers.common.Authorization;
        delete axios.defaults.headers.post.Authorization;
    }
};

export const clearAuthSession = () => {
    setAccessToken(null);
    setStoredUser(null);
    window.localStorage.removeItem("user");
};

export const persistAuthResponse = (data) => {
    if (!data?.access_token || !data?.params) {
        return null;
    }

    resetAuthSessionInvalidation();
    setAccessToken(data.access_token);
    setStoredUser(data.params);

    return data.params;
};

export const refreshAccessToken = async () => {
    if (sessionInvalidated) {
        return null;
    }

    const response = await axios.post(
        `${baseUrl}php/models/login/refresh.php`,
        new FormData(),
        { withCredentials: true }
    );

    if (response.data?.error === 0 && response.data?.access_token) {
        return persistAuthResponse(response.data);
    }

    return null;
};

/** Один refresh на все параллельные 401/403-ответы с error: 3. */
export const refreshAccessTokenOnce = async () => {
    if (sessionInvalidated) {
        return null;
    }

    if (refreshInFlight) {
        return refreshInFlight;
    }

    refreshInFlight = refreshAccessToken().finally(() => {
        refreshInFlight = null;
    });

    return refreshInFlight;
};

export const logoutOnServer = async () => {
    invalidateAuthSession();

    try {
        await axios.post(
            `${baseUrl}php/models/login/logout.php`,
            new FormData(),
            { withCredentials: true }
        );
    } catch {
        // ignore network errors on logout
    }

    clearAuthSession();
};
